// src/FrontEnd_Certification_In_Noida.tsx

import React, { useState } from "react"; // Import useState
import { motion } from "framer-motion";

import { FaLaptopCode, FaCertificate, FaBriefcase, FaFileAlt, FaComments, FaUsers } from 'react-icons/fa';
import { Helmet } from "react-helmet-async";
import frontImg from "../../assets/Internship/FrontEnd.png";
import JoinNowFormPopup from "../../componants/JoinNowFormPopup"; // Corrected typo: components -> componants

import html from "../../assets/Internship/html.jpg";
import css from "../../assets/Internship/css.svg";
import javascript from "../../assets/Internship/javascript.png";
import Reactjs from "../../assets/Internship/React_logo.png";
import Bootstrap from "../../assets/Internship/bootstrap.jpg";
import AngulerJs from "../../assets/Internship/anguler.png";

import img from "../../assets/Internship/our-process.webp";

// Animation variants
const fadeInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const FrontEndCertificationInNoida: React.FC = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
  const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title

  const handleOpenPopup = (courseTitle: string) => {
    setSelectedCourseTitle(courseTitle);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedCourseTitle(""); // Clear selected course when closing
  };

  const whyChooseUsBenefits = [
    {
      icon: <FaLaptopCode className="text-blue-400 text-5xl mb-4" />,
      title: "Hybrid Mode Class",
      description: "Learn flexibly with combined online and offline classroom training sessions.",
    },
    {
      icon: <FaCertificate className="text-blue-400 text-5xl mb-4" />,
      title: "Internship Certificate",
      description: "Receive an internship certificate for hands-on industry project experience.",
    },
    {
      icon: <FaBriefcase className="text-blue-400 text-5xl mb-4" />,
      title: "100% Placement Assistance",
      description: "Complete placement support provided until you secure a suitable job.",
    },
    {
      icon: <FaFileAlt className="text-blue-400 text-5xl mb-4" />,
      title: "CV Building & ATS Score",
      description: "Optimize your resume for ATS and improve shortlisting chances significantly.",
    },
    {
      icon: <FaComments className="text-blue-400 text-5xl mb-4" />,
      title: "Mock Interviews",
      description: "Practice with mock interviews to boost real interview performance confidence.",
    },
    {
      icon: <FaUsers className="text-blue-400 text-5xl mb-4" />,
      title: "15 Students Per Batch",
      description: "Small batches ensure personalized attention and better student-teacher interaction.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 font-sans antialiased">
      <Helmet>
        <title>Summer Training & Frontend Certification in Noida | Appwars Technologies</title>
        <meta name="description" content="Transform your summer into a career boost with top-rated Summer Training and Frontend Certification in Noida. Specializing in Web Development, Data Analytics, and other IT courses, offering 100% placement, hybrid classes, and internship certificates by Appwars Technologies." />
        <meta name="keywords" content="summer training, frontend certification, web development, data analytics, Noida, ReactJS, Machine Learning, Python, Java, MERN, IoT, Robotics, AI, digital marketing, Power BI, training, placement, internship, Appwars Technologies, Nadezhda Ventures" />
        <meta name="author" content="Appwars Technologies / Nadezhda Ventures" />
        <meta property="og:title" content="Summer Training & Frontend Certification in Noida | Appwars Technologies" />
        <meta property="og:description" content="Boost your career with comprehensive summer training and IT certification programs in Noida." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="http://yourwebsite.com/summer-training-frontend-noida" />
        <link rel="canonical" href="http://yourwebsite.com/summer-training-frontend-noida" />
      </Helmet>

      <main>
        {/* SECTION 1 - Hero Section with Transparent Image and Text Overlay */}
        <section className="relative overflow-hidden flex items-center justify-center h-[400px] md:h-[400px] lg:h-[400px]">
          <div className="absolute inset-0 z-0">
            <img
              src="https://png.pngtree.com/thumb_back/fh260/background/20190223/ourmid/pngtree-blue-fluttering-technology-background-boardbackground-image_83398.jpg"
              alt="Flutter Transparent Background"
              className="w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-black opacity-70"></div>
          </div>

          <div className="min-h-screen flex flex-col md:flex-row items-center justify-center gap-12 px-6 py-20 max-w-7xl mx-auto relative z-10">
            <motion.div
              className="flex-1 text-center md:text-left"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInLeft}
            >
              <h1 className="text-4xl md:text-5xl font-extrabold text-orange-500 mb-6">
                FrontEnd Development Certification
              </h1>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Master the art of web interface creation with Nadezhda Venture's comprehensive certification program. Build dynamic, responsive, and visually stunning user experiences.
              </p>

              {/* Modified Join Now button to open the popup */}
              <button
                className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                onClick={() => handleOpenPopup("FrontEnd Development Certification")} // Pass the course title
              >
                Join Now !
              </button>
            </motion.div>

            <motion.div
              className="flex-1 w-full max-w-2xl flex justify-center items-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInRight}
            >
              <img
                src={frontImg}
                alt="Front End Showcase"
                className="rounded-xl shadow-lg w-full h-auto object-contain max-h-[400px]"
              />
            </motion.div>
          </div>
        </section>

        {/* Popular Courses Section */}
        <section className="px-6 md:px-20 py-24 bg-gray-300 text-orange-600">
          <h2 className="text-5xl font-bold text-center mb-10">Popular Courses</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {/* HTML */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={html} alt="HTML Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">HTML</h3>
              <button
                onClick={() => handleOpenPopup("HTML")} // Pass the course title
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">
                Join Now
              </button>
            </div>

            {/* CSS */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={css} alt="CSS Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">CSS</h3>
              <button
                onClick={() => handleOpenPopup("CSS")} // Pass the course title
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">
                Join Now
              </button>
            </div>

            {/* JavaScript */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={javascript} alt="JavaScript Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">JavaScript</h3>
              <button
                onClick={() => handleOpenPopup("JavaScript")} // Pass the course title
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">
                Join Now
              </button>
            </div>

            {/* React Js */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={Reactjs} alt="React Js Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">React Js</h3>
              <button
                onClick={() => handleOpenPopup("React Js")} // Pass the course title
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">
                Join Now
              </button>
            </div>

            {/* Bootstrap */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={Bootstrap} alt="Bootstrap Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">BootStrap</h3>
              <button
                onClick={() => handleOpenPopup("BootStrap")} // Pass the course title
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">
                Join Now
              </button>
            </div>

            {/* Angular Js */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={AngulerJs} alt="Angular Js Icon" className="w-26 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">Anguler Js</h3>
              <button
                onClick={() => handleOpenPopup("Angular Js")} // Pass the course title
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">
                Join Now
              </button>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section (from image_ab7e7d.png, styling adapted) */}
        <section className="px-6 md:px-20 py-16 md:py-24 bg-white text-gray-800">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Why Choose Us?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyChooseUsBenefits.map((benefit, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-6 bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                {benefit.icon}
                <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Our Achievements Section replaced with a single image */}
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-orange-500">Our Interview Proccess</h2>
        <img
          src={img}
          alt="Our Achievements"
          className="w-full h-auto object-cover"
        />
      </main>


      {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default FrontEndCertificationInNoida;